import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const CartContext = createContext();
const API_URL = process.env.REACT_APP_BACKEND_URL;

const loadJson = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const saveJson = (key, value) => {
  if (value === null || value === undefined) {
    localStorage.removeItem(key);
  } else {
    localStorage.setItem(key, JSON.stringify(value));
  }
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  // Lazy initializers read localStorage once at mount (before any save happens).
  const [cart, setCartState] = useState(() => loadJson('sunify_cart', []));
  const [currency, setCurrencyState] = useState(() => localStorage.getItem('sunify_currency') || 'EUR');
  const [coupon, setCouponState] = useState(() => loadJson('sunify_coupon', null));

  // Persist on change (safe now because initial state already matches localStorage).
  useEffect(() => { saveJson('sunify_cart', cart); }, [cart]);
  useEffect(() => { localStorage.setItem('sunify_currency', currency); }, [currency]);
  useEffect(() => { saveJson('sunify_coupon', coupon); }, [coupon]);

  const setCart = setCartState;
  const setCurrency = setCurrencyState;
  const setCoupon = setCouponState;

  const addToCart = (product, quantity = 1) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.id === product.id);
      if (existingItem) {
        return prevCart.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prevCart, { ...product, quantity }];
    });
    // GA4 event
    if (typeof window.gtag === 'function') {
      const price = Number(product.price_eur) || 0;
      window.gtag('event', 'add_to_cart', {
        currency: 'EUR',
        value: +(price * quantity).toFixed(2),
        items: [{
          item_id: product.sku || product.id,
          item_name: product.translations?.sk?.name || product.name,
          price,
          quantity,
        }],
      });
    }
  };

  const removeFromCart = (productId) => {
    setCart(prevCart => prevCart.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(prevCart =>
      prevCart.map(item =>
        item.id === productId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = useCallback(() => {
    setCart([]);
    setCoupon(null);
  }, [setCart, setCoupon]);

  // Считает итог по позициям с учётом оптовой скидки за каждые полные
  // bulk_threshold_m (по умолчанию 30 mb) — единая логика с backend.
  const getLineTotal = (item) => {
    const price = currency === 'EUR' ? Number(item.price_eur || 0) : Number(item.price_usd || 0);
    const qty = Number(item.quantity || 0);
    if (qty <= 0 || price <= 0) return { base: 0, discount: 0, final: 0 };

    const unit = item.unit || 'mb';
    if (unit !== 'mb') {
      const base = price * qty;
      return { base, discount: 0, final: base };
    }

    const threshold = Number(item.bulk_threshold_m ?? 30);
    const discountPct = Number(item.bulk_discount_percent ?? 20);
    if (threshold <= 0) {
      const base = price * qty;
      return { base, discount: 0, final: base };
    }
    const fullChunks = Math.floor(qty / threshold);
    const bulkQty = fullChunks * threshold;
    const remainder = +(qty - bulkQty).toFixed(4);
    const bulkTotal = bulkQty * price * (1 - discountPct / 100);
    const remainderTotal = remainder * price;
    const base = price * qty;
    const final = +(bulkTotal + remainderTotal).toFixed(2);
    return {
      base: +base.toFixed(2),
      discount: +(base - final).toFixed(2),
      final,
    };
  };

  const getSubtotal = () => {
    return cart.reduce((total, item) => total + getLineTotal(item).final, 0);
  };

  const getBulkDiscount = () => {
    return cart.reduce((sum, item) => sum + getLineTotal(item).discount, 0);
  };

  const getCouponDiscount = () => {
    if (!coupon || !cart.length) return 0;
    // Product-bound coupon: only discount items of that product (or whole cart if untied)
    if (coupon.product_id) {
      const inCart = cart.some(i => i.id === coupon.product_id);
      if (!inCart) return 0;
    }
    return (getSubtotal() * (coupon.discount_percent || 0)) / 100;
  };

  const getTotal = () => Math.max(0, getSubtotal() - getCouponDiscount());

  const applyCoupon = async (code, email = null) => {
    const trimmed = (code || '').trim().toUpperCase();
    if (!trimmed) return { ok: false, reason: 'empty_code' };
    try {
      const res = await axios.post(`${API_URL}/api/coupons/validate`, {
        code: trimmed,
        email: email || undefined,
        product_ids: cart.map(i => i.id),
        subtotal: getSubtotal(),
      });
      if (res.data?.valid) {
        setCoupon(res.data);
        return { ok: true, data: res.data };
      }
      return {
        ok: false,
        reason: res.data?.reason || 'invalid',
        min_order_total: res.data?.min_order_total,
      };
    } catch (err) {
      return { ok: false, reason: 'network_error' };
    }
  };

  const removeCoupon = () => setCoupon(null);

  const getItemCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        currency,
        setCurrency,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getTotal,
        getSubtotal,
        getCouponDiscount,
        getBulkDiscount,
        getLineTotal,
        getItemCount,
        coupon,
        applyCoupon,
        removeCoupon,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};